import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useHistory, useLocation, useParams } from 'react-router-dom';
import { articlesApi, dictionaryApi, journalsApi, releasesApi } from '../../api/endpoints';
import {
    Checkbox,
    FileField,
    FormActions,
    FormSection,
    MultiSelect,
    SingleSelect,
    TextArea,
    TextInput,
} from '../../components/Form';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';
import { useUi } from '../../context/UiContext';
import { listUrl } from '../../hooks/listReturn';
import useDictionaryCreate from '../../hooks/useDictionaryCreate';
import styles from '../listPage.module.css';

const EMPTY = {
    name: '',
    description: '',
    udk: '',
    journalId: '',
    releaseId: '',
    authors: [],
    keywords: [],
    view: true,
};

/** Новая статья может открываться уже привязанной к выпуску: /admin/articles/new?journal=2&release=15 */
const initialFromQuery = (search) => {
    const q = new URLSearchParams(search);
    return {
        ...EMPTY,
        journalId: q.get('journal') ? Number(q.get('journal')) : '',
        releaseId: q.get('release') ? Number(q.get('release')) : '',
    };
};

const newInReleaseUrl = (journalId, releaseId) =>
    `/admin/articles/new?journal=${journalId}&release=${releaseId}`;

/**
 * Форма статьи — одна на создание и на редактирование.
 *
 * При редактировании подставляются связи (журнал, выпуск, авторы, ключевые слова).
 * Новых авторов и ключевые слова можно завести прямо из полей. Статьи одного
 * выпуска удобно вводить подряд: «Добавить и создать ещё в этот выпуск».
 */
const ArticleForm = () => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const { toast } = useUi();
    const isEdit = Boolean(id);

    const [values, setValues] = useState(() => (isEdit ? EMPTY : initialFromQuery(location.search)));
    const [saved, setSaved] = useState(values); // снимок для проверки «есть ли несохранённое»
    const [file, setFile] = useState(null);
    const [currentFile, setCurrentFile] = useState(null);

    const [journals, setJournals] = useState([]);
    const [releases, setReleases] = useState([]);
    const [authorOptions, setAuthorOptions] = useState([]);
    const [keywordOptions, setKeywordOptions] = useState([]);

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(0);
    const [errors, setErrors] = useState({});
    const [loadError, setLoadError] = useState(null);

    const nameRef = useRef(null);
    const leavingRef = useRef(false); // переход сразу после сохранения — не спрашивать

    // Один и тот же экземпляр формы переиспользуется при переходе
    // «редактирование → новая статья»: сбрасываем поля под новую запись.
    useEffect(() => {
        if (isEdit) return;
        const init = initialFromQuery(location.search);
        setValues(init);
        setSaved(init);
        setFile(null);
        setCurrentFile(null);
        setErrors({});
        setLoading(false);
        leavingRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit]);

    const createAuthor = useDictionaryCreate('authors', setAuthorOptions, 'автор');
    const createKeyword = useDictionaryCreate('keywords', setKeywordOptions, 'ключевое слово');

    const dirty = useMemo(
        () => !loading && (file !== null || JSON.stringify(values) !== JSON.stringify(saved)),
        [loading, file, values, saved]
    );

    const set = useCallback((key, value) => {
        setValues((v) => ({ ...v, [key]: value }));
        setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    }, []);

    // Справочники и журналы нужны и при создании, и при редактировании.
    useEffect(() => {
        let cancelled = false;

        Promise.all([
            dictionaryApi.options('authors'),
            dictionaryApi.options('keywords'),
            journalsApi.list({ perPage: 200, sort: 'name', dir: 'asc' }),
        ])
            .then(([authors, keywords, journalPage]) => {
                if (cancelled) return;
                setAuthorOptions(authors.map((a) => ({ value: a.id, label: a.name })));
                setKeywordOptions(keywords.map((k) => ({ value: k.id, label: k.name })));
                setJournals(journalPage.items.map((j) => ({ value: j.id, label: j.name })));
            })
            .catch((e) => !cancelled && toast.error(`Не удалось загрузить справочники: ${e.message}`));

        return () => {
            cancelled = true;
        };
    }, [toast]);

    useEffect(() => {
        if (!isEdit) return undefined;

        let cancelled = false;
        setLoading(true);
        leavingRef.current = false;

        articlesApi
            .get(id)
            .then((item) => {
                if (cancelled) return;
                const loaded = {
                    name: item.name || '',
                    description: item.description || '',
                    udk: item.udk || '',
                    journalId: item.release && item.release.journal ? item.release.journal.id : '',
                    releaseId: item.release ? item.release.id : '',
                    authors: item.authors.map((a) => a.id),
                    keywords: item.keywords.map((k) => k.id),
                    view: item.view === 1,
                };
                setValues(loaded);
                setSaved(loaded);
                setCurrentFile(item.file_path || null);
            })
            .catch((e) => !cancelled && setLoadError(e))
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
    }, [id, isEdit]);

    // Выпуски подгружаются под выбранный журнал.
    useEffect(() => {
        if (!values.journalId) {
            setReleases([]);
            return undefined;
        }

        let cancelled = false;
        releasesApi
            .list({ journal_id: values.journalId, perPage: 200, sort: 'date', dir: 'desc' })
            .then((page) => {
                if (cancelled) return;
                setReleases(
                    page.items.map((r) => ({
                        value: r.id,
                        label: `№${r.number || '—'}${r.tome ? `, том ${r.tome}` : ''}${
                            r.date ? ` (${r.date.slice(0, 4)})` : ''
                        }`,
                    }))
                );
            })
            .catch((e) => !cancelled && toast.error(`Не удалось загрузить выпуски: ${e.message}`));

        return () => {
            cancelled = true;
        };
    }, [values.journalId, toast]);

    const save = async ({ andAddAnother = false } = {}) => {
        setErrors({});
        setSaving(true);
        setProgress(0);

        const payload = {
            name: values.name,
            description: values.description,
            udk: values.udk,
            release_id: values.releaseId || '',
            authors: values.authors,
            keywords: values.keywords,
            view: values.view,
        };
        if (file) payload.file = file;

        try {
            if (isEdit) {
                await articlesApi.update(id, payload, setProgress);
                toast.success('Изменения сохранены');
                setSaved(values);
                setFile(null);
                // Перечитываем, чтобы показать актуальный путь к файлу после замены.
                const fresh = await articlesApi.get(id);
                setCurrentFile(fresh.file_path || null);
                return;
            }

            const created = await articlesApi.create(payload, setProgress);

            if (andAddAnother) {
                // Следующая статья того же выпуска: журнал и выпуск остаются, остальное — с нуля.
                const next = { ...EMPTY, journalId: values.journalId, releaseId: values.releaseId, view: values.view };
                toast.success(`Статья «${values.name}» добавлена. Можно вводить следующую.`);
                setValues(next);
                setSaved(next);
                setFile(null);
                history.replace(newInReleaseUrl(values.journalId, values.releaseId));
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => nameRef.current && nameRef.current.focus(), 50);
                return;
            }

            toast.success('Статья добавлена');
            setSaved(values);
            setFile(null);
            leavingRef.current = true;
            history.push(`/admin/articles/${created.id}`);
        } catch (err) {
            if (err.isValidation) {
                setErrors(err.errors || {});
                toast.error('Проверьте заполнение полей');
            } else {
                toast.error(err.message);
            }
        } finally {
            setSaving(false);
            setProgress(0);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        save();
    };

    if (loadError) {
        return (
            <div className="adm-card adm-card-body">
                <p>Не удалось загрузить статью: {loadError.message}</p>
                <NavLink to={listUrl('/admin/articles')} className="adm-btn adm-btn-secondary">
                    К списку статей
                </NavLink>
            </div>
        );
    }

    const canAddToRelease = Boolean(values.journalId && values.releaseId);

    return (
        <div className={styles.formPage}>
            <UnsavedChangesPrompt when={dirty && !saving} bypassRef={leavingRef} />

            <NavLink to={listUrl('/admin/articles')} className={styles.backLink}>
                ← К списку статей
            </NavLink>

            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">{isEdit ? 'Редактирование статьи' : 'Новая статья'}</h1>
                    {isEdit && <p className="adm-page-subtitle">{values.name}</p>}
                </div>
                {isEdit && !loading && (
                    <div className={styles.headerLinks}>
                        <a href={`/журнал/статья/${id}`} target="_blank" rel="noopener noreferrer">
                            Открыть на сайте ↗
                        </a>
                        {canAddToRelease && (
                            <NavLink to={newInReleaseUrl(values.journalId, values.releaseId)}>
                                + Ещё статья в этот выпуск
                            </NavLink>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="adm-card">
                    <div className={styles.formLoading}>Загрузка данных статьи…</div>
                </div>
            ) : (
                <form className="adm-card" onSubmit={handleSubmit}>
                    <FormSection title="Основное">
                        <TextInput
                            id="article-name"
                            label="Название"
                            required
                            ref={nameRef}
                            value={values.name}
                            onChange={(e) => set('name', e.target.value)}
                            error={errors.name && errors.name[0]}
                            placeholder="Название статьи"
                        />

                        <TextArea
                            id="article-description"
                            label="Аннотация"
                            value={values.description}
                            onChange={(e) => set('description', e.target.value)}
                            error={errors.description && errors.description[0]}
                            rows={5}
                        />

                        <TextInput
                            id="article-udk"
                            label="УДК"
                            value={values.udk}
                            onChange={(e) => set('udk', e.target.value)}
                            error={errors.udk && errors.udk[0]}
                            placeholder="например, 338.22.021.4"
                        />
                    </FormSection>

                    <FormSection
                        title="Размещение"
                        description="Сначала выберите журнал — список выпусков подставится под него."
                    >
                        <div className="adm-grid-2">
                            <SingleSelect
                                label="Журнал"
                                options={journals}
                                value={values.journalId}
                                onChange={(v) => {
                                    set('journalId', v);
                                    set('releaseId', '');
                                }}
                                placeholder="— выберите журнал —"
                            />

                            <SingleSelect
                                label="Выпуск"
                                options={releases}
                                value={values.releaseId}
                                onChange={(v) => set('releaseId', v)}
                                placeholder={values.journalId ? '— выберите выпуск —' : 'сначала выберите журнал'}
                                error={errors.release_id && errors.release_id[0]}
                            />
                        </div>
                    </FormSection>

                    <FormSection title="Связи">
                        <MultiSelect
                            label="Авторы"
                            options={authorOptions}
                            value={values.authors}
                            onChange={(v) => set('authors', v)}
                            onCreate={createAuthor}
                            hint="Порядок авторов сохраняется. Нет в списке — наберите имя и выберите «+ Добавить»."
                        />

                        <MultiSelect
                            label="Ключевые слова"
                            options={keywordOptions}
                            value={values.keywords}
                            onChange={(v) => set('keywords', v)}
                            onCreate={createKeyword}
                        />
                    </FormSection>

                    <FormSection title="Файл и публикация">
                        <FileField
                            id="article-file"
                            label="Документ (PDF)"
                            accept="application/pdf"
                            currentPath={currentFile}
                            file={file}
                            onChange={setFile}
                            progress={progress}
                            error={errors.file && errors.file[0]}
                            hint={
                                isEdit
                                    ? 'Если файл не выбран, текущий останется без изменений.'
                                    : 'Можно добавить позже.'
                            }
                        />

                        <Checkbox
                            id="article-view"
                            label="Показывать на сайте"
                            checked={values.view}
                            onChange={(v) => set('view', v)}
                            hint="Скрытые статьи остаются в админке, но не видны посетителям."
                        />
                    </FormSection>

                    <FormActions>
                        <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>
                            {saving && <span className="adm-spinner" />}
                            {saving ? 'Сохранение…' : isEdit ? 'Сохранить изменения' : 'Добавить статью'}
                        </button>
                        {!isEdit && (
                            <button
                                type="button"
                                className="adm-btn adm-btn-secondary"
                                disabled={saving || !canAddToRelease}
                                title={canAddToRelease ? undefined : 'Сначала выберите журнал и выпуск'}
                                onClick={() => save({ andAddAnother: true })}
                            >
                                Добавить и ввести ещё в этот выпуск
                            </button>
                        )}
                        <NavLink to={listUrl('/admin/articles')} className="adm-btn adm-btn-secondary">
                            Отмена
                        </NavLink>
                        {saving && progress > 0 && progress < 100 && (
                            <span className="adm-muted">Загрузка файла: {progress}%</span>
                        )}
                    </FormActions>
                </form>
            )}
        </div>
    );
};

export default ArticleForm;
